import type { EventReminder } from 'models';
import { useMemo } from 'react';
import { generateUploadFile, getDatePickerDateType } from 'ui';
import { useGetMediaSource } from 'features/media';
import { useFormatTime, useGenerateDefaultReminderHeroImage } from '../../../hooks';
import type { EditReminderFormValues } from '../EditReminderForm';

export const useGetInitialValues = (event: EventReminder) => {
  const getMediaSource = useGetMediaSource();
  const formatTime = useFormatTime();
  const defaultHeroImage = useGenerateDefaultReminderHeroImage();

  return useMemo((): Partial<EditReminderFormValues> => {
    const { allDay: isAllDay, coverPhoto, html, name, preloadedFiles, startTime, text } = event;
    const heroImage = coverPhoto
      ? generateUploadFile({
          response: {
            id: coverPhoto,
            url: getMediaSource({ id: coverPhoto }),
          },
        })
      : defaultHeroImage;

    const attachments = preloadedFiles
      ? {
          fileList: preloadedFiles.map((file) => ({
            fileName: file.filename,
            name: file.filename,
            percent: 100,
            response: file,
            size: file.byteSize,
            status: 'done' as const,
            type: file.contentType,
            uid: file.id,
            url: file.url,
          })),
        }
      : undefined;

    return {
      attachments,
      heroImage,
      htmlDescription: html,
      isAllDay: isAllDay ?? false,
      name,
      startDate: getDatePickerDateType(startTime),
      startTime: formatTime(startTime),
      textDescription: text,
    };
  }, [defaultHeroImage, event, formatTime, getMediaSource]);
};
